import React, {useState, useEffect} from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  useMediaQuery,
  Grid,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Today as TodayIcon,
  CalendarMonth,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';

// Typ für einen einzelnen Urlaubsantrag
interface Urlaubsantrag {
  id: string;
  username: string;
  beginn: string;
  ende: string;
  grund: string;
  notiz?: string;
  status: string;
  bearbeitung_datum?: string;
  bearbeiter_name?: string;
}

// Props für den Urlaubskalender
interface UrlaubskalenderProps {
  loading: boolean;
  antraege: Urlaubsantrag[];
}

export const Urlaubskalender: React.FC<UrlaubskalenderProps> = ({loading, antraege}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // State für den aktuell angezeigten Monat
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [hoveredAntrag, setHoveredAntrag] = useState<Urlaubsantrag | null>(null);

  // Initialisiere dayjs mit deutscher Lokalisierung
  useEffect(() => {
    dayjs.locale('de');
  }, []);

  // Navigiere zum vorherigen Monat
  const goToPreviousMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  // Navigiere zum nächsten Monat
  const goToNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  // Navigiere zum aktuellen Monat
  const goToCurrentMonth = () => {
    setCurrentDate(dayjs());
  };

  // Generiere Kalendertage für den aktuellen Monat
  const generateCalendarDays = () => {
    const daysInMonth = currentDate.daysInMonth();
    const firstDayOfMonth = currentDate.startOf('month').day(); // 0 = Sonntag, 1 = Montag, ...

    // Anpassen, damit Montag der erste Tag der Woche ist
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    const days = [];

    // Leere Zellen für Tage vor dem ersten Tag des Monats
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(null);
    }

    // Tage des aktuellen Monats
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  // Prüfe, ob ein bestimmter Tag einen Urlaubsantrag hat
  const getUrlaubForDay = (day: number) => {
    if (!day) return [];

    const date = currentDate.date(day);

    return antraege.filter((antrag) => {
      const beginnDate = dayjs(antrag.beginn);
      const endeDate = dayjs(antrag.ende);

      return (
        date.isSame(beginnDate, 'day') ||
        date.isSame(endeDate, 'day') ||
        (date.isAfter(beginnDate, 'day') && date.isBefore(endeDate, 'day'))
      );
    });
  };

  // Überprüft, ob ein Tag der Anfang eines Urlaubsantrags ist
  const isStartOfUrlaub = (day: number, antrag: Urlaubsantrag) => {
    if (!day) return false;
    const date = currentDate.date(day);
    return date.isSame(dayjs(antrag.beginn), 'day');
  };

  // Überprüft, ob ein Tag das Ende eines Urlaubsantrags ist
  const isEndOfUrlaub = (day: number, antrag: Urlaubsantrag) => {
    if (!day) return false;
    const date = currentDate.date(day);
    return date.isSame(dayjs(antrag.ende), 'day');
  };

  // Überprüft, ob ein Tag in der Mitte eines Urlaubsantrags ist
  const isMiddleOfUrlaub = (day: number, antrag: Urlaubsantrag) => {
    if (!day) return false;
    const date = currentDate.date(day);
    return date.isAfter(dayjs(antrag.beginn), 'day') && date.isBefore(dayjs(antrag.ende), 'day');
  };

  // Bestimmt die Position eines Tages in einer Urlaubsperiode
  const getUrlaubPosition = (day: number, antrag: Urlaubsantrag) => {
    if (!day) return '';

    const isStart = isStartOfUrlaub(day, antrag);
    const isEnd = isEndOfUrlaub(day, antrag);
    const isMiddle = isMiddleOfUrlaub(day, antrag);

    // Vereinfachte Logik für die Positionierung
    if (isStart && isEnd) return 'single';
    if (isStart) return 'start';
    if (isEnd) return 'end';
    if (isMiddle) return 'middle';
    return '';
  };

  // Erstelle ein konsistentes Mapping von Urlaub zu Position in der Zelle
  const createUrlaubPositionMapping = () => {
    const mapping = new Map<string, number>();
    let position = 0;

    // Gruppiere Anträge nach Benutzernamen
    const userAntraege = new Map<string, Urlaubsantrag[]>();

    antraege.forEach((antrag) => {
      if (!userAntraege.has(antrag.username)) {
        userAntraege.set(antrag.username, []);
      }
      userAntraege.get(antrag.username)?.push(antrag);
    });

    // Sortiere Benutzer alphabetisch
    const sortedUsers = Array.from(userAntraege.keys()).sort();

    // Weise jedem Benutzer-Antrag eine feste Position zu
    sortedUsers.forEach((username) => {
      const userUrlaube = userAntraege.get(username) || [];

      // Sortiere die Anträge des Benutzers nach Beginn-Datum
      userUrlaube
        .sort((a, b) => dayjs(a.beginn).valueOf() - dayjs(b.beginn).valueOf())
        .forEach((antrag) => {
          mapping.set(antrag.id, position);
          position += 1;
        });
    });

    return mapping;
  };

  // Erstelle ein Mapping von Benutzernamen zu Position
  const createUserPositionMapping = () => {
    const mapping = new Map<string, number>();

    // Gruppiere Anträge nach Benutzernamen
    const uniqueUsernames = [...new Set(antraege.map((antrag) => antrag.username))].sort();

    // Weise jedem Benutzer eine feste Position zu
    uniqueUsernames.forEach((username, index) => {
      mapping.set(username, index);
    });

    return mapping;
  };

  // Generiere eine eindeutige Farbe für jeden Benutzer
  const getUserColor = (username: string) => {
    // Einfache Hash-Funktion für den Benutzernamen
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Konvertiere Hash zu HEX-Farbe und stelle sicher, dass sie hell genug ist
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      // Stelle sicher, dass der Wert zwischen 100 und 200 liegt (helle, aber nicht zu helle Farben)
      const adjustedValue = Math.min(Math.max(100, value), 200).toString(16);
      color += ('00' + adjustedValue).substr(-2);
    }

    return color;
  };

  // Erhalte eindeutige Benutzernamen für die Legende
  const getUniqueUsers = () => {
    const users = new Set<string>();
    antraege.forEach((antrag) => users.add(antrag.username));
    return Array.from(users);
  };

  // Generiert eine Legende mit Farben und Benutzernamen
  const renderLegend = () => {
    const uniqueUsers = getUniqueUsers();

    return (
      <Box sx={{mb: isMobile ? 2 : 3}}>
        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{mb: 1}}>
          {isMobile ? 'Mitarbeiter:' : 'Mitarbeiter im Urlaub:'}
        </Typography>
        <Grid container spacing={isMobile ? 1 : 2}>
          {uniqueUsers.map((user) => (
            <Grid item key={user}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: getUserColor(user),
                  borderRadius: 1,
                  px: isMobile ? 1 : 1.5,
                  py: isMobile ? 0.5 : 0.75,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                  transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                  '&:hover': {
                    boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                  },
                }}>
                {!isMobile && <PersonIcon sx={{mr: 0.5, color: 'white'}} />}
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    textShadow: '0px 1px 2px rgba(0,0,0,0.2)',
                  }}>
                  {user}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  const calendarDays = generateCalendarDays();
  const weekdays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  // Mobiles Layout
  if (isMobile) {
    return (
      <Box sx={{p: 1}}>
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            borderRadius: 1,
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
            }}>
            <IconButton onClick={goToPreviousMonth} sx={{color: '#fff'}}>
              <ChevronLeftIcon />
            </IconButton>
            <Box display='flex' alignItems='center'>
              <CalendarMonth sx={{mr: 1}} />
              <Typography variant='h6' sx={{fontWeight: 500}}>
                {currentDate.format('MMMM YYYY')}
              </Typography>
            </Box>
            <Box display='flex'>
              <IconButton
                onClick={goToCurrentMonth}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  mr: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.2)',
                  },
                }}>
                <TodayIcon />
              </IconButton>
              <IconButton onClick={goToNextMonth} sx={{color: '#fff'}}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* Legende - Mobile */}
        {renderLegend()}

        {/* Liste der Urlaubsanträge nach Datum sortiert */}
        <Typography variant='subtitle1' sx={{mb: 1}}>
          Urlaubsübersicht:
        </Typography>
        {antraege
          .sort((a, b) => dayjs(a.beginn).valueOf() - dayjs(b.beginn).valueOf())
          .map((antrag) => {
            const userColor = getUserColor(antrag.username);
            return (
              <Paper
                key={antrag.id}
                sx={{
                  mb: 1,
                  p: 1.5,
                  borderLeft: `4px solid ${userColor}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                  transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                  '&:hover': {
                    boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                  },
                }}>
                <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: userColor,
                      mr: 1,
                    }}
                  />
                  <Typography variant='subtitle2'>{antrag.username}</Typography>
                </Box>
                <Box display='flex' alignItems='center' mt={1}>
                  <Chip
                    size='small'
                    label={`${dayjs(antrag.beginn).format('DD.MM.YYYY')} - ${dayjs(
                      antrag.ende
                    ).format('DD.MM.YYYY')}`}
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      bgcolor: 'rgba(0,0,0,0.05)',
                    }}
                  />
                </Box>
                {antrag.grund && (
                  <Typography variant='body2' color='text.secondary' mt={0.5}>
                    Grund: {antrag.grund}
                  </Typography>
                )}
              </Paper>
            );
          })}
      </Box>
    );
  }

  // Desktop Layout
  return (
    <Box sx={{p: 2}}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          backgroundColor: theme.palette.primary.main,
          color: '#fff',
          borderRadius: 1,
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
          }}>
          <IconButton onClick={goToPreviousMonth} sx={{color: '#fff'}}>
            <ChevronLeftIcon />
          </IconButton>
          <Box display='flex' alignItems='center'>
            <CalendarMonth sx={{mr: 1}} />
            <Typography variant='h5' sx={{fontWeight: 500}}>
              {currentDate.format('MMMM YYYY')}
            </Typography>
          </Box>
          <Box display='flex'>
            <IconButton
              onClick={goToCurrentMonth}
              sx={{
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.1)',
                mr: 1,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                },
              }}>
              <TodayIcon />
            </IconButton>
            <IconButton onClick={goToNextMonth} sx={{color: '#fff'}}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Legende - Desktop */}
      {renderLegend()}

      {/* Kalender */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor: 'rgba(25, 118, 210, 0.08)'}}>
              {weekdays.map((day) => (
                <TableCell
                  key={day}
                  align='center'
                  sx={{
                    fontWeight: 'bold',
                    padding: '12px 8px',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: theme.palette.primary.main,
                  }}>
                  {day}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({length: Math.ceil(calendarDays.length / 7)}).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({length: 7}).map((_, colIndex) => {
                  const dayIndex = rowIndex * 7 + colIndex;
                  const day = calendarDays[dayIndex];
                  const urlaubsForDay = getUrlaubForDay(day ?? 0);
                  const isToday =
                    day && dayjs().month() === currentDate.month() && dayjs().date() === day;
                  const isWeekend = colIndex >= 5; // Sa & So

                  return (
                    <TableCell
                      key={colIndex}
                      align='center'
                      sx={{
                        height: '150px',
                        width: '14%',
                        position: 'relative',
                        padding: '6px 1px',
                        ...(day && {
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                        }),
                        ...(isToday && {
                          bgcolor: 'rgba(25, 118, 210, 0.1)',
                          border: `2px solid ${theme.palette.primary.main}`,
                        }),
                        ...(isWeekend &&
                          day && {
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                          }),
                      }}>
                      {day && (
                        <>
                          <Typography
                            sx={{
                              position: 'absolute',
                              top: 5,
                              left: 5,
                              fontWeight: isToday ? 'bold' : 'normal',
                              color: isToday
                                ? theme.palette.primary.main
                                : isWeekend
                                ? 'text.secondary'
                                : 'inherit',
                              backgroundColor: isToday ? 'rgba(255,255,255,0.7)' : 'transparent',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              ...(isToday && {
                                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                              }),
                              zIndex: 2,
                            }}>
                            {day}
                          </Typography>

                          <Box
                            sx={{
                              mt: 4,
                              minHeight: `${
                                22 * getUniqueUsers().length +
                                (getUniqueUsers().length > 0 ? 10 : 0)
                              }px`,
                              display: 'grid',
                              gridTemplateRows: `repeat(${getUniqueUsers().length}, 22px)`,
                              gap: '2px',
                              position: 'relative',
                            }}>
                            {/* Erstelle für jeden Benutzer einen reservierten Platz */}
                            {getUniqueUsers()
                              .sort()
                              .map((username, index) => {
                                // Filtere Anträge dieses Benutzers für den aktuellen Tag
                                const userAntraegeForDay = urlaubsForDay.filter(
                                  (antrag) => antrag.username === username
                                );

                                // Wenn keine Anträge für diesen Benutzer an diesem Tag existieren, zeige eine leere Zelle
                                if (userAntraegeForDay.length === 0) {
                                  return (
                                    <Box
                                      key={`placeholder-${username}`}
                                      sx={{
                                        gridRow: index + 1,
                                        height: '22px',
                                      }}
                                    />
                                  );
                                }

                                // Sonst zeige den entsprechenden Antrag
                                const antrag = userAntraegeForDay[0]; // Normalerweise sollte pro Benutzer und Tag nur ein Antrag sein
                                const position = getUrlaubPosition(day, antrag);
                                const userColor = getUserColor(antrag.username);

                                return (
                                  <Tooltip
                                    key={antrag.id}
                                    title={
                                      <Box>
                                        <Typography variant='body2'>
                                          <strong>{antrag.username}</strong>
                                        </Typography>
                                        <Typography variant='body2'>
                                          {dayjs(antrag.beginn).format('DD.MM.YYYY')} -{' '}
                                          {dayjs(antrag.ende).format('DD.MM.YYYY')}
                                        </Typography>
                                        {antrag.grund && (
                                          <Typography variant='body2'>
                                            Grund: {antrag.grund}
                                          </Typography>
                                        )}
                                        {antrag.notiz && (
                                          <Typography variant='body2'>
                                            Notiz: {antrag.notiz}
                                          </Typography>
                                        )}
                                        <Typography variant='body2'>
                                          Status: {antrag.status}
                                        </Typography>
                                      </Box>
                                    }
                                    onOpen={() => setHoveredAntrag(antrag)}
                                    onClose={() => setHoveredAntrag(null)}
                                    arrow>
                                    <Box
                                      sx={{
                                        bgcolor: userColor,
                                        color: 'white',
                                        p: 0.5,
                                        height: '22px',
                                        width: '100%',
                                        gridRow: index + 1, // Position im Grid entsprechend dem Benutzerindex
                                        fontSize: '0.75rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        zIndex: 1,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                        '&:hover': {
                                          filter: 'brightness(1.1)',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                                        },
                                        ...(position === 'single' && {
                                          borderRadius: '4px',
                                          fontWeight: 500,
                                        }),
                                        ...(position === 'start' && {
                                          borderRadius: '4px 0 0 4px',
                                          fontWeight: 500,
                                        }),
                                        ...(position === 'end' && {
                                          borderRadius: '0 4px 4px 0',
                                        }),
                                        ...(position === 'middle' && {
                                          borderRadius: '0',
                                        }),
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}>
                                      {/* Nur am Anfang Text anzeigen */}
                                      {position === 'start' || position === 'single'
                                        ? antrag.username
                                        : ''}
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                          </Box>
                        </>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
